import { gsap } from 'gsap';
// Auto-discovered list of every .mp4 in /public/video/ — populated by the
// `video-pool` Vite plugin in vite.config.js. Drop a file in the folder and
// the dev server reloads with it included.
import DISCOVERED_VIDEO_POOL from 'virtual:video-pool';

/**
 * BackgroundManager
 * ─────────────────────────────────────────────────────────────
 * Owns the glitchGL instance applied to .background__video.
 *
 * Five presets that mirror the glitchGL demo set:
 *   clean    — quiet rest state, faint scanlines only
 *   crt      — D3 vibe: arcade monitor, scanlines + curvature + bloom
 *   pixel    — D4 vibe: pixelation + bit-depth crunch
 *   digital  — D1 vibe: glitch module heavy (rgbShift, sync errors, datamosh)
 *   multiple — D2 vibe: all three modules at moderate levels
 *
 * Each preset also defines its own mouse-interaction shape + which params
 * the cursor velocity drives, so the "personality" extends to hover too.
 *
 * Public API:
 *   setPreset(name)              // switch preset, snaps via updateOptions
 *   randomize()                  // pick a new preset != current, spike, swap source
 *   triggerGlitch({intensity})   // spike → ease back to current baseline
 *   rotateSource() / setSource(src)
 *   destroy()
 */

// Fall back to a small known-good set if discovery returns empty (e.g. if
// the folder ever ships missing). In normal operation this is the entire
// /public/video/ directory.
const FALLBACK_POOL = ['./video/5.mp4'];
const VIDEO_POOL = DISCOVERED_VIDEO_POOL.length ? DISCOVERED_VIDEO_POOL : FALLBACK_POOL;

// Every numeric param we want to lerp between peak ↔ baseline during a spike.
// If a preset doesn't define one, it falls through to 0 (off).
const LERP_KEYS = {
  pixelation: ['pixelSize'],
  crt: ['scanlineIntensity', 'chromaticAberration', 'curvature', 'phosphorGlow', 'brightness'],
  glitch: [
    'rgbShift', 'digitalNoise', 'lineDisplacement', 'bitCrushDepth',
    'signalDropoutFreq', 'signalDropoutSize',
    'syncErrorFreq', 'syncErrorAmount',
    'interferenceIntensity', 'frameGhostAmount', 'stutterFreq', 'datamoshStrength',
  ],
};

// ── Resting baseline ─────────────────────────────────────────
// Shared starting point for every preset. Each preset overrides only
// what it needs — e.g. crt bumps phosphor + curvature, pixel enables
// the pixelation pass, etc. Resting state is intentionally near-invisible:
// the glitchGL effect should read as "clean video with a whisper of
// texture" until the randomizer fires.
const REST = {
  pixelation: {
    enabled: false,
    pixelSize: 1,
    pixelShape: 'square',
    bitDepth: '8-bit',
    dithering: 'none',
    pixelDirection: 'square',
  },
  crt: {
    enabled: true,
    preset: 'broadcast-monitor',
    scanlineIntensity: 0.02,
    scanlineThickness: 0.2,
    scanlineCount: 600,
    phosphorGlow: 0.03,
    curvature: 0.0,
    chromaticAberration: 0.0002,
    brightness: 1.0,
    flicker: false,
    lineMovement: false,
    lineSpeed: 0,
    lineDirection: 'down',
  },
  glitch: {
    enabled: false,
    rgbShift: 0,
    digitalNoise: 0,
    lineDisplacement: 0,
    bitCrushDepth: 8.0,
    signalDropoutFreq: 0,
    signalDropoutSize: 0,
    syncErrorFreq: 0,
    syncErrorAmount: 0,
    interferenceSpeed: 0,
    interferenceIntensity: 0,
    frameGhostAmount: 0,
    stutterFreq: 0,
    datamoshStrength: 0,
  },
};

// Helper: build a preset by merging overrides on top of REST.
// Object spread is shallow, so each section spreads its own REST defaults.
const preset = ({ pixelation = {}, crt = {}, glitch = {} } = {}) => ({
  pixelation: { ...REST.pixelation, ...pixelation },
  crt: { ...REST.crt, ...crt },
  glitch: { ...REST.glitch, ...glitch },
});

// Default global intensity used until the first preset takes over.
// Per-preset `intensity` (below) gets applied via setIntensity() on
// each setPreset() call, so each preset has its own hover bite without
// affecting the resting state. Higher = louder cursor effect.
const DEFAULT_INTENSITY = 3.0;

const PRESETS = {
  // ── clean ────────────────────────────────────────────────
  // Pure REST. Reads as the source video with a faint scanline whisper.
  // Hover needs to amplify near-zero baselines, so intensity runs high.
  clean: {
    label: 'clean',
    intensity: 5.0,                           // baselines tiny → big mult
    interaction: {
      shape: 'square',
      customSize: '50vw',
      velocity: false,
      effects: {
        pixelation: [],
        crt: ['chromaticAberration', 'phosphorGlow', 'scanlineIntensity'],
        glitch: [],
      },
    },
    effects: preset(),
  },

  // ── crt ──────────────────────────────────────────────────
  // Subtle arcade-monitor character: phosphor glow + gentle bezel curve.
  // Baselines already noticeable, so intensity is moderate.
  crt: {
    label: 'crt',
    intensity: 3.5,
    interaction: {
      shape: 'circle',
      customSize: '45vw',
      effects: {
        pixelation: [],
        crt: ['chromaticAberration', 'curvature', 'scanlineIntensity', 'phosphorGlow', 'brightness'],
        glitch: [],
      },
    },
    effects: preset({
      crt: {
        scanlineIntensity: 0.06,
        phosphorGlow: 0.08,
        curvature: 1.5,
      },
    }),
  },

  // ── pixel ────────────────────────────────────────────────
  // Mild pixelation only. Hover should make the cursor area chunkier.
  // pixelSize gets multiplied by intensity, so 5x makes a real impact.
  pixel: {
    label: 'pixel',
    intensity: 5.0,
    interaction: {
      shape: 'circle',
      customSize: '40vw',
      effects: {
        pixelation: ['pixelSize'],
        crt: ['chromaticAberration', 'phosphorGlow'],
        glitch: [],
      },
    },
    effects: preset({
      pixelation: {
        enabled: true,
        pixelSize: 2,
      },
    }),
  },

  // ── digital ──────────────────────────────────────────────
  // Glitch module sub-perceptible at rest — hover is where it lives.
  // Highest intensity in the set so the cursor leaves a real RGB tear.
  digital: {
    label: 'digital',
    intensity: 6.0,                           // strongest cursor bite
    interaction: {
      shape: 'circle',
      customSize: '50vw',
      effects: {
        pixelation: [],
        crt: ['chromaticAberration'],
        glitch: ['rgbShift', 'digitalNoise', 'lineDisplacement', 'interferenceIntensity', 'frameGhostAmount'],
      },
    },
    effects: preset({
      glitch: {
        enabled: true,
        rgbShift: 0.0008,
        digitalNoise: 0.002,
        lineDisplacement: 0.001,
        interferenceIntensity: 0.001,
      },
    }),
  },

  // ── multiple ─────────────────────────────────────────────
  // Pixelation + glitch both quietly active. Mid-range intensity since
  // multiple effects multiplied together can stack quickly.
  multiple: {
    label: 'multiple',
    intensity: 4.0,
    interaction: {
      shape: 'circle',
      customSize: '55vw',
      effects: {
        pixelation: ['pixelSize'],
        crt: ['chromaticAberration', 'phosphorGlow'],
        glitch: ['rgbShift', 'digitalNoise', 'lineDisplacement'],
      },
    },
    effects: preset({
      pixelation: {
        enabled: true,
        pixelSize: 1.5,
      },
      glitch: {
        enabled: true,
        rgbShift: 0.0006,
        digitalNoise: 0.002,
      },
    }),
  },
};

const PRESET_NAMES = Object.keys(PRESETS);

// Spike values applied on triggerGlitch — the brief flash on dice /
// canvas click. Resting state is intentionally invisible; the spike is
// where the personality lives. Tuned to read as a sharp pop, not a tear.
// Anything not listed here lerps from REST → REST (stays at 0).
const PEAK = {
  pixelation: { pixelSize: 4 },
  crt: {
    scanlineIntensity: 0.08,
    chromaticAberration: 0.002,
  },
  glitch: {
    rgbShift: 0.003,
    digitalNoise: 0.06,
    lineDisplacement: 0.008,
    datamoshStrength: 0.08,
    frameGhostAmount: 0.06,
    interferenceIntensity: 0.04,
  },
};

// Dice / channel change: a much harder hit than triggerGlitch's click pop.
// PEAK × this, plus the sync/dropout params PEAK leaves at 0.
const CHANNEL_CHANGE_INTENSITY = 4.5;
const CHANNEL_CHANGE_PIXELS = 14;
const CHANNEL_CHANGE_EXTRA = {
  syncErrorFreq: 0.9,
  syncErrorAmount: 0.35,
  signalDropoutFreq: 0.6,
  signalDropoutSize: 0.25,
  stutterFreq: 0.5,
  bitCrushDepth: 3.0,
  interferenceSpeed: 8.0,
};

export class BackgroundManager {
  constructor(target = '.background__video') {
    this.target = target;
    this.instance = null;
    this._tween = null;
    this._destroyed = false;
    this._videoEl = null;
    this._currentSrc = null;

    this._currentPresetName = 'clean';
    this._baseline = PRESETS.clean.effects;

    this._init();
  }

  // ── boot ────────────────────────────────────────────────────────────

  _init() {
    if (typeof window === 'undefined') return;
    // A world (the game) covers the whole viewport. Decoding the background
    // clip and running the glitch shader under it is the single biggest
    // drain on the game's frame rate, so both stop until the world closes.
    document.addEventListener('world:open', () => this.suspend());
    document.addEventListener('world:close', () => this.resume());
    const ready = () =>
      typeof window.glitchGL === 'function' && typeof window.THREE === 'object';

    if (ready()) {
      this._mount();
    } else {
      const wait = () => {
        if (this._destroyed) return;
        if (ready()) this._mount();
        else requestAnimationFrame(wait);
      };
      wait();
    }
  }

  _mount() {
    this._videoEl = document.querySelector(this.target);
    if (this._videoEl) {
      this._currentSrc = this._videoEl.getAttribute('src');
      // Random first channel — a different clip every visit, swapped before
      // the loader lifts so the hardcoded src never reads as "the" opener.
      if (VIDEO_POOL.length > 1) {
        const choices = VIDEO_POOL.filter((s) => s !== this._currentSrc);
        const pick = choices[(Math.random() * choices.length) | 0];
        if (pick) {
          this._currentSrc = pick;
          this._videoEl.src = pick;
          this._videoEl.load();
        }
      }
      this._videoEl.play?.().catch(() => { });
    }

    const preset = PRESETS[this._currentPresetName];

    try {
      this.instance = window.glitchGL({
        target: this.target,
        // Initial intensity comes from the starting preset. Subsequent
        // setPreset() calls swap it via this.setIntensity(p.intensity)
        // so each preset has its own hover bite. Higher = louder cursor.
        intensity: preset.intensity ?? DEFAULT_INTENSITY,
        // Preserve the source video's intrinsic dimensions on the canvas so
        // CSS object-fit: cover can crop natively.
        aspectCorrection: true,
        interaction: {
          enabled: true,
          shape: preset.interaction.shape,
          customSize: preset.interaction.customSize,
          // Proximity-based, NOT velocity-based — effect responds to where
          // the cursor is, not how fast it moves. Slow hover now triggers it.
          velocity: false,
          effects: preset.interaction.effects,
        },
        effects: preset.effects,
        on: {
          // glitchGL calls this with the internal controller (has updateOptions);
          // kick video + a render pass so the first paint isn’t a dead loop
          // (inView/0×0) that only recovers on click/dice.
          init: () => {
            queueMicrotask(() => this._kickInitialPlayback());
          },
        },
      });
    } catch (err) {
      console.warn('[BackgroundManager] glitchGL init failed:', err);
    }

    this._bindClick();
    this._bindLoopCounter();
    this._probeClipAudio();
  }

  // After a clip has looped this many times, change the channel on its own.
  static MAX_LOOPS = 3;

  /**
   * The <video> is `loop`, so there's no `ended` — detect the wrap by
   * currentTime jumping backwards. Third wrap → swap the clip under a
   * channel-change spike (video only: theme, audio, preset stay).
   */
  _bindLoopCounter() {
    const v = this._videoEl;
    if (!v) return;
    this._loops = 0;
    this._lastT = 0;
    v.addEventListener('timeupdate', () => {
      if (v.srcObject || this._changing) { this._lastT = v.currentTime; return; }
      if (v.currentTime + 0.5 < this._lastT) {
        this._loops++;
        if (this._loops >= BackgroundManager.MAX_LOOPS) this.autoChange();
      }
      this._lastT = v.currentTime;
    });
    v.addEventListener('loadstart', () => { this._loops = 0; this._lastT = 0; });
  }

  /** Video-only channel change (used by the loop counter). */
  async autoChange() {
    if (!this.instance || this._changing) { this.rotateSource(); return; }
    this._changing = true;
    this._tween?.kill();
    const peak = scaledPeak(CHANNEL_CHANGE_INTENSITY * 0.7);
    this._update({
      pixelation: { enabled: true, ...peak.pixelation, pixelSize: 10 },
      crt: { ...peak.crt },
      glitch: { enabled: true, ...peak.glitch, ...CHANNEL_CHANGE_EXTRA },
    });
    this.rotateSource();
    await this._awaitFirstFrame(900);
    const baseline = this._baseline;
    const start = { pixelation: { ...peak.pixelation, pixelSize: 10 }, crt: { ...peak.crt }, glitch: { ...peak.glitch, ...CHANNEL_CHANGE_EXTRA } };
    await new Promise((resolve) => {
      const proxy = { t: 0 };
      this._tween = gsap.to(proxy, {
        t: 1, duration: 0.7, ease: 'power3.out',
        onUpdate: () => this._update(lerpedConfig(start, baseline, proxy.t)),
        onComplete: () => { this._update(baseline); resolve(); },
      });
    });
    this._changing = false;
    document.dispatchEvent(new CustomEvent('bg:auto-change', { detail: { src: this._currentSrc } }));
  }

  /**
   * Ensure the hidden source <video> is playing and nudge glitchGL to render
   * after layout/video frames exist (avoids black canvas until interaction).
   */
  _kickInitialPlayback() {
    if (this._destroyed) return;
    this._videoEl = document.getElementById('bg-video') || document.querySelector(this.target);
    if (!this._videoEl) return;
    this._currentSrc = this._videoEl.currentSrc
      || this._videoEl.getAttribute('src')
      || this._currentSrc;
    this._videoEl.muted = true;
    this._videoEl.defaultMuted = true;
    this._videoEl.setAttribute('playsinline', '');
    this._videoEl.setAttribute('muted', '');

    const tryPlay = () => {
      this._videoEl?.play?.().catch(() => {});
      if (this.instance?.updateOptions) {
        try {
          this.instance.updateOptions({ intensity: PRESETS[this._currentPresetName]?.intensity ?? DEFAULT_INTENSITY });
        } catch {
          /* ignore */
        }
      }
    };

    if (this._videoEl.readyState >= 2) tryPlay();
    else {
      this._videoEl.addEventListener('loadeddata', tryPlay, { once: true });
      this._videoEl.addEventListener('canplay', tryPlay, { once: true });
    }
    tryPlay();
    requestAnimationFrame(() => requestAnimationFrame(tryPlay));
    window.addEventListener(
      'load',
      () => {
        if (this._destroyed) return;
        tryPlay();
        try {
          window.dispatchEvent(new Event('resize'));
        } catch {
          /* ignore */
        }
      },
      { once: true }
    );
  }

  /**
   * Click anywhere on the page (except UI controls) → cycle the glitch
   * preset only. The dice button still does the full randomize.
   *
   * We listen at document level rather than on #background because the
   * background element sits beneath .main in z-order, so direct clicks
   * land on .main first. Bubbling reaches us either way.
   */
  _bindClick() {
    document.addEventListener('click', this._onPageClick);
    // Subtle cursor hint that the surface is interactive.
    const host = document.getElementById('background');
    if (host) host.style.cursor = 'pointer';
  }

  // Anything matching this selector is treated as UI and won't trigger a cycle.
  _UI_SELECTOR = 'a, button, input, textarea, select, label, [role="button"], .sidebar, .card';

  _onPageClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest(this._UI_SELECTOR)) return;
    this.cyclePreset();
  };

  // ── public API ──────────────────────────────────────────────────────

  /** List of available preset names — handy for debugging / UI. */
  get presets() { return PRESET_NAMES.slice(); }
  get currentPreset() { return this._currentPresetName; }

  /**
   * Switch to a named preset. Snaps interaction config + effects via glitchGL,
   * AND writes data-theme on <body> so _themes.scss can swap the color +
   * font palette in lockstep with the visual preset. Single source of truth.
   */
  setPreset(name) {
    const p = PRESETS[name];
    if (!p) return false;

    this._currentPresetName = name;
    this._baseline = p.effects;

    // Theme cascade — every UI element that reads var(--accent), --accent-dim,
    // --font-display, --font-condensed, --font-mono updates instantly.
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-theme', name);
    }

    if (!this.instance?.updateOptions) return true;
    try {
      // Swap interaction config + effects, then sync the per-preset
      // intensity so hover bite matches the new preset's character.
      this.instance.updateOptions({
        intensity: p.intensity ?? DEFAULT_INTENSITY,
        interaction: {
          shape: p.interaction.shape,
          customSize: p.interaction.customSize,
          effects: p.interaction.effects,
        },
        effects: p.effects,
      });
    } catch (err) {
      console.warn('[BackgroundManager] setPreset failed:', err);
    }
    return true;
  }

  /**
   * Cycle to a different glitch preset only. Does NOT touch the video
   * source, audio, or theme — used by the canvas click ("change the filter,
   * keep the channel").
   * Returns the chosen preset name.
   */
  cyclePreset() {
    const choices = PRESET_NAMES.filter((n) => n !== this._currentPresetName);
    if (!choices.length) return this._currentPresetName;
    const next = choices[(Math.random() * choices.length) | 0];
    this.setPreset(next);
    this.triggerGlitch({ intensity: 1 });
    return next;
  }

  /**
   * Dice: change the channel for real. Slam the signal into a heavy
   * channel-change spike (datamosh, sync errors, dropouts, RGB tear, chunky
   * pixels), swap the clip *under* that noise, wait for the new clip's first
   * frame, then pick the new preset + theme and let the spike decay onto it.
   * The swap is never seen raw — only through static.
   * Returns the chosen preset name (resolves after the spike settles).
   */
  async randomize() {
    if (!this.instance) {
      this.rotateSource();
      return this._currentPresetName;
    }
    this._tween?.kill();
    this._changing = true;

    // Heavy spike, held while the new clip loads.
    const peak = scaledPeak(CHANNEL_CHANGE_INTENSITY);
    this._update({
      pixelation: { enabled: true, ...peak.pixelation, pixelSize: CHANNEL_CHANGE_PIXELS },
      crt: { ...peak.crt },
      glitch: { enabled: true, ...peak.glitch, ...CHANNEL_CHANGE_EXTRA },
    });

    this.rotateSource();
    await this._awaitFirstFrame(900);

    // Land on the new preset (theme swaps in lockstep) and decay the spike.
    const choices = PRESET_NAMES.filter((n) => n !== this._currentPresetName);
    const next = choices[(Math.random() * choices.length) | 0] || this._currentPresetName;
    this.setPreset(next);
    const baseline = this._baseline;
    const start = {
      pixelation: { ...peak.pixelation, pixelSize: CHANNEL_CHANGE_PIXELS },
      crt: { ...peak.crt },
      glitch: { ...peak.glitch, ...CHANNEL_CHANGE_EXTRA },
    };

    await new Promise((resolve) => {
      const proxy = { t: 0 };
      this._tween = gsap.to(proxy, {
        t: 1,
        duration: 0.8,
        ease: 'power3.out',
        delay: 0.06,
        onUpdate: () => {
          // Stutter the decay so it reads as a signal locking in, not a fade.
          const k = proxy.t < 0.35 ? proxy.t * (Math.random() < 0.25 ? 0.2 : 1) : proxy.t;
          this._update(lerpedConfig(start, baseline, k));
        },
        onComplete: () => {
          this._update(baseline);
          resolve();
        },
      });
    });
    this._changing = false;
    return next;
  }

  /** Resolve when the source video has a decoded frame (or after `ms`). */
  _awaitFirstFrame(ms = 900) {
    const v = this._videoEl;
    if (!v) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const fin = () => { if (!done) { done = true; resolve(); } };
      if (typeof v.requestVideoFrameCallback === 'function') v.requestVideoFrameCallback(fin);
      else v.addEventListener('loadeddata', fin, { once: true });
      v.addEventListener('error', fin, { once: true });
      setTimeout(fin, ms);
    });
  }

  /** Pick a random clip (different from current) and swap. */
  rotateSource() {
    if (!this._videoEl || VIDEO_POOL.length < 2) return null;
    const choices = VIDEO_POOL.filter((s) => s !== this._currentSrc);
    const next = choices[(Math.random() * choices.length) | 0];
    this.setSource(next);
    return next;
  }

  /**
   * After a clip has played ~0.5s, report whether it carries audio so the
   * page can decide to put the tape on air (see 'bg:clip-audio').
   */
  _probeClipAudio() {
    const v = this._videoEl;
    if (!v || v.srcObject) return;
    const src = this._currentSrc;
    let done = false;
    const check = () => {
      if (done || this._videoEl !== v || this._currentSrc !== src) return;
      done = true;
      const has = !!(v.mozHasAudio || (v.webkitAudioDecodedByteCount > 0) || (v.audioTracks && v.audioTracks.length));
      const name = (src.split('/').pop() || '').replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').toUpperCase();
      document.dispatchEvent(new CustomEvent('bg:clip-audio', { detail: { video: v, src, name, hasAudio: has } }));
    };
    // Chrome only fills webkitAudioDecodedByteCount once frames decode.
    const onPlaying = () => setTimeout(check, 600);
    v.addEventListener('playing', onPlaying, { once: true });
    setTimeout(() => { v.removeEventListener('playing', onPlaying); check(); }, 2500);
  }

  setSource(src) {
    if (!this._videoEl || !src || src === this._currentSrc) return;
    if (this._videoEl.srcObject) {
      // Tuning away from the live (webcam) channel.
      this._videoEl.srcObject = null;
      document.dispatchEvent(new CustomEvent('bg:source-change', { detail: { src } }));
    }
    this._currentSrc = src;
    this._videoEl.muted = true;
    this._videoEl.src = src;
    this._videoEl.load();
    this._videoEl.play?.().catch(() => { });
    this._probeClipAudio();
  }

  /**
   * Live channel: feed a MediaStream (webcam) through the same pipeline.
   * Pass null to tune back to a random clip. Both directions go through
   * the heavy channel-change spike so the swap is never seen raw.
   */
  setLiveStream(stream) {
    if (!this._videoEl) return;
    this._tween?.kill();
    const peak = scaledPeak(CHANNEL_CHANGE_INTENSITY);
    this._update({
      pixelation: { enabled: true, ...peak.pixelation, pixelSize: CHANNEL_CHANGE_PIXELS },
      crt: { ...peak.crt },
      glitch: { enabled: true, ...peak.glitch, ...CHANNEL_CHANGE_EXTRA },
    });
    if (stream) {
      this._videoEl.removeAttribute('src');
      this._videoEl.srcObject = stream;
      this._videoEl.muted = true;
      this._videoEl.play?.().catch(() => {});
    } else {
      this._videoEl.srcObject = null;
      this._currentSrc = null;
      this.rotateSource();
    }
    const settle = () => {
      const baseline = this._baseline;
      const start = {
        pixelation: { ...peak.pixelation, pixelSize: CHANNEL_CHANGE_PIXELS },
        crt: { ...peak.crt },
        glitch: { ...peak.glitch, ...CHANNEL_CHANGE_EXTRA },
      };
      const proxy = { t: 0 };
      this._tween = gsap.to(proxy, {
        t: 1, duration: 0.9, ease: 'power3.out', delay: 0.1,
        onUpdate: () => this._update(lerpedConfig(start, baseline, proxy.t)),
        onComplete: () => this._update(baseline),
      });
    };
    this._awaitFirstFrame(1200).then(settle);
  }

  /**
   * Spike glitch + pixelation, then GSAP-ease back to whatever preset is
   * currently set as the baseline.
   */
  triggerGlitch({ intensity = 1 } = {}) {
    if (!this.instance) return;
    this._tween?.kill();

    // Snapshot baseline at the start so a mid-tween preset change doesn't
    // pull the lerp targets out from under us.
    const baseline = this._baseline;
    const peak = scaledPeak(intensity);

    // Slam to peak, with pixelation force-enabled so it always shows during spike
    this._update({
      pixelation: { enabled: true, ...peak.pixelation },
      crt: { ...peak.crt },
      glitch: { enabled: true, ...peak.glitch },
    });

    const proxy = { t: 0 };
    this._tween = gsap.to(proxy, {
      t: 1,
      duration: 0.55,
      ease: 'power3.out',
      delay: 0.08, // brief peak hold
      onUpdate: () => {
        const k = proxy.t;
        this._update(lerpedConfig(peak, baseline, k));
      },
      onComplete: () => {
        // Snap to canonical baseline (also re-applies enabled flags)
        this._update(baseline);
      },
    });
  }

  /**
   * Magnet warp: k in 0..1.4 bends the whole tube (curvature, fringing,
   * line tear) on top of the current baseline; 0 restores it.
   */
  setWarp(k) {
    if (!this.instance) return;
    const b = this._baseline;
    if (k <= 0) { this._update(b); this.setIntensity(PRESETS[this._currentPresetName]?.intensity ?? DEFAULT_INTENSITY); return; }
    this._tween?.kill();
    this.setIntensity((PRESETS[this._currentPresetName]?.intensity ?? DEFAULT_INTENSITY) * (1 + 1.1 * k));
    this._update({
      pixelation: { ...b.pixelation },
      crt: { ...b.crt, curvature: 1.5 + 6 * k, chromaticAberration: 0.004 * k, phosphorGlow: 0.12 * k, brightness: 1 + 0.15 * k },
      glitch: { ...b.glitch, enabled: true, rgbShift: 0.0025 * k, lineDisplacement: 0.006 * k, interferenceIntensity: 0.03 * k, frameGhostAmount: 0.08 * k },
    });
  }

  setIntensity(intensity) {
    if (!this.instance?.updateOptions) return;
    try {
      this.instance.updateOptions({ intensity });
    } catch (err) {
      console.warn('[BackgroundManager] setIntensity failed:', err);
    }
  }

  /**
   * Wait until the source video has a decoded frame and glitchGL has been
   * nudged to composite — used before the world → home pixel reveal so the
   * end state is not a black #background then a popping video.
   */
  /** Stop the video and hide the glitch canvas while something covers the page. */
  suspend() {
    if (this._suspended || this._destroyed) return;
    this._suspended = true;
    try { this._videoEl?.pause(); } catch { /* ignore */ }
    const host = document.getElementById('background');
    if (host) host.style.display = 'none';
  }

  /** Back on: show the canvas, restart the clip and nudge the shader into rendering again. */
  resume() {
    if (!this._suspended || this._destroyed) return;
    this._suspended = false;
    const host = document.getElementById('background');
    if (host) host.style.display = '';
    this._kickInitialPlayback();
  }

  async prepareForHomeTransition() {
    if (this._destroyed) return;
    this.resume(); // the home reveal needs the background running under the wipe
    this._videoEl = document.getElementById('bg-video') || document.querySelector(this.target);
    if (!this._videoEl) return;

    this._videoEl.muted = true;
    this._currentSrc = this._videoEl.currentSrc
      || this._videoEl.getAttribute('src')
      || this._currentSrc;

    const nudgeGlitch = () => {
      if (!this.instance?.updateOptions) return;
      try {
        this.instance.updateOptions({
          intensity: PRESETS[this._currentPresetName]?.intensity ?? DEFAULT_INTENSITY,
        });
      } catch {
        /* ignore */
      }
    };

    const tryPlay = () => {
      this._videoEl?.play?.().catch(() => {});
      nudgeGlitch();
    };

    tryPlay();

    await new Promise((resolve) => {
      const v = this._videoEl;
      if (v.readyState >= 2) {
        queueMicrotask(resolve);
        return;
      }
      const done = () => resolve();
      v.addEventListener('loadeddata', done, { once: true });
      v.addEventListener('canplay', done, { once: true });
      v.addEventListener('error', done, { once: true });
      setTimeout(done, 2500);
    });

    tryPlay();

    await new Promise((resolve) => {
      const v = this._videoEl;
      let finished = false;
      const fin = () => {
        if (finished) return;
        finished = true;
        resolve();
      };
      if (typeof v.requestVideoFrameCallback === 'function') {
        v.requestVideoFrameCallback(fin);
        setTimeout(fin, 500);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(fin));
      }
    });

    nudgeGlitch();
    try {
      window.dispatchEvent(new Event('resize'));
    } catch {
      /* ignore */
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    nudgeGlitch();
  }

  destroy() {
    this._destroyed = true;
    this._tween?.kill();
    document.removeEventListener('click', this._onPageClick);
    this.instance?.cleanup?.();
    this.instance = null;
  }

  // ── internal ────────────────────────────────────────────────────────

  _update(effects) {
    if (!this.instance?.updateOptions) return;
    try {
      this.instance.updateOptions({ effects });
    } catch (err) {
      console.warn('[BackgroundManager] updateOptions failed:', err);
    }
  }
}

// ── helpers ──────────────────────────────────────────────────────────

function scaledPeak(intensity) {
  const out = { pixelation: {}, crt: {}, glitch: {} };
  for (const [section, keys] of Object.entries(LERP_KEYS)) {
    for (const k of keys) {
      const v = PEAK[section]?.[k];
      if (v != null) out[section][k] = v * intensity;
    }
  }
  return out;
}

function lerpedConfig(peak, baseline, k) {
  const out = { pixelation: {}, crt: {}, glitch: {} };
  for (const [section, keys] of Object.entries(LERP_KEYS)) {
    for (const key of keys) {
      const a = peak[section]?.[key] ?? baseline[section]?.[key] ?? 0;
      const b = baseline[section]?.[key] ?? 0;
      out[section][key] = a + (b - a) * k;
    }
  }
  return out;
}
